import { useEffect, useState } from 'react'
import { MuiOtpInput, MuiOtpInputProps } from 'mui-one-time-password-input'
import { Box, Link, Typography } from '@mui/material'
import { useCountdown } from '@/hooks'
import {
  useReadUserProfile,
  useSendAliasVerification,
  useValidateAlias,
} from '@/api/user-manager'
import { Loading } from '@/components'
interface VerificationCodeProps extends MuiOtpInputProps {
  type: 'email' | 'phone'
  onSuccess?: () => void
}

const SECONDS_TO_RESEND = 60

export function VerificationCode({
  type,
  onSuccess,
  ...props
}: VerificationCodeProps) {
  const [verificationCode, setVerificationCode] = useState('')

  const { data: user, isLoading: readingUserProfile } = useReadUserProfile()

  const emailUnverified = user?.alias?.find(
    (alias) => alias.type === 'email' && alias.verified === false,
  )?.data

  const { mutateAsync: sendVerificationEmail } = useSendAliasVerification()

  const { mutateAsync: validateAlias, isError: validateEmailError } =
    useValidateAlias({
      onSuccess: () => onSuccess?.(),
    })

  const { count, startCountdown } = useCountdown({
    countStart: SECONDS_TO_RESEND,
  })

  const handleChangeVerificationCode = (newValue: string) => {
    setVerificationCode(newValue)
  }

  const handleCompleteVerificationCode = (token: string) => {
    if (!user || !emailUnverified) return

    if (type === 'email') {
      validateAlias({
        alias: emailUnverified,
        alias_type: 'email',
        token_value: token,
      })
    }
  }

  const sendVerificationCode = () => {
    if (!user || !emailUnverified) return

    if (type === 'email') {
      sendVerificationEmail({
        alias: emailUnverified,
        alias_type: 'email',
      })
    }
  }

  const handleResubmitCode = () => {
    sendVerificationCode()
    startCountdown()
  }

  const isInvalidCode = validateEmailError

  useEffect(() => {
    if (!user) return
    sendVerificationCode()
  }, [user])

  if (readingUserProfile) {
    return <Loading />
  }

  if (!user) {
    return
  }

  return (
    <Box textAlign="center">
      <Typography mb={2}>
        Enviamos um código para <b>{type === 'email' ? emailUnverified : ''}</b>
      </Typography>

      <MuiOtpInput
        value={verificationCode}
        onChange={handleChangeVerificationCode}
        onComplete={handleCompleteVerificationCode}
        autoFocus
        {...props}
      />

      {isInvalidCode && (
        <Typography color="error" mt={1}>
          Código inválido
        </Typography>
      )}

      <Typography variant="body2" mt={2}>
        {count === SECONDS_TO_RESEND ? (
          <>
            Ainda não recebeu?{' '}
            <Link onClick={handleResubmitCode}>Reenviar</Link>
          </>
        ) : (
          `Aguarde ${count} para reenviar.`
        )}
      </Typography>
    </Box>
  )
}
